import React, { useEffect, useState } from 'react';
import { Card } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { eventsService } from '../../../core/services/eventsService';
import { EventLocation, EventType } from '../../../types';
import styles from './SchedulerSetting.module.scss';
import EventLocationsList from './EventsSetting/EventLocationsList';
import EventTypesList from './EventsSetting/EventTypesList';
import CreateEventLocation from './EventsSetting/CreateEventLocation';
import CreateEventType from './EventsSetting/CreateEventType';
import EditEventLocation from './EventsSetting/EditEventLocation';
import EditEventType from './EventsSetting/EditEventType';
import { CircularProgress, Box } from '@mui/material';
import { usePermissionCheck } from '../../../core/hooks/usePermissionCheck';

type ViewMode =
	| 'list'
	| 'createEventLocation'
	| 'createEventType'
	| 'editEventLocation'
	| 'editEventType';

const EventsSetting = () => {
	const { hasViewPermission, hasEditPermission, hasDeletePermission, hasExecutePermission } = usePermissionCheck();
	const canViewEvents = hasViewPermission('Events');
	const canEditEvents = hasEditPermission('Events');
	const canDeleteEvents = hasDeletePermission('Events');
	const canExecuteEvents = hasExecutePermission('Events');
	const [viewMode, setViewMode] = useState<ViewMode>('list');
	const [eventLocations, setEventLocations] = useState<EventLocation[]>([]);
	const [eventTypes, setEventTypes] = useState<EventType[]>([]);
	const [editingEventLocation, setEditingEventLocation] = useState<EventLocation | null>(null);
	const [editingEventType, setEditingEventType] = useState<EventType | null>(null);

	const [loadingLocations, setLoadingLocations] = useState(false);
	const [loadingTypes, setLoadingTypes] = useState(false);

	useEffect(() => {
		if (canViewEvents) {
			loadData();
		}
	}, [canViewEvents]);

	const loadData = async () => {
		setLoadingLocations(true);
		setLoadingTypes(true);

		try {
			const [locationsData, typesData] = await Promise.all([
				eventsService.getEventLocations(),
				eventsService.getEventTypes(),
			]);
			setEventLocations(locationsData || []);
			setEventTypes(typesData || []);
			setViewMode('list');
		} finally {
			setLoadingLocations(false);
			setLoadingTypes(false);
		}
	};

	const handleBackClick = () => {
		setViewMode('list');
		setEditingEventLocation(null);
		setEditingEventType(null);
	};

	if (!canViewEvents) {
		return (
			<div className={styles.schedulerSettings}>
				<Card className="card">
					<div style={{ padding: '20px', textAlign: 'center' }}>
						You do not have permission to view events settings.
					</div>
				</Card>
			</div>
		);
	}

	if (viewMode === 'createEventLocation') {
		return (
			<div className={styles.schedulerSettings}>
				<CreateEventLocation backButtonClick={handleBackClick} onEventLocationCreated={loadData} />
			</div>
		);
	}

	if (viewMode === 'editEventLocation' && editingEventLocation) {
		return (
			<div className={styles.schedulerSettings}>
				<EditEventLocation
					location={editingEventLocation}
					handleBackClick={handleBackClick}
					onEventLocationUpdated={loadData}
				/>
			</div>
		);
	}

	if (viewMode === 'createEventType') {
		return (
			<div className={styles.schedulerSettings}>
				<CreateEventType backButtonClick={handleBackClick} onEventTypeCreated={loadData} />
			</div>
		);
	}

	if (viewMode === 'editEventType' && editingEventType) {
		return (
			<div className={styles.schedulerSettings}>
				<EditEventType
					type={editingEventType}
					handleBackClick={handleBackClick}
					onEventTypeUpdated={loadData}
				/>
			</div>
		);
	}

	return (
		<div className={styles.schedulerSettings}>
			<Card className="card">
				<div className={styles.header}>
					<h2 className={styles.formTitle}>Event Locations</h2>
					<Button
						className={styles.addButton}
						onClick={() => setViewMode('createEventLocation')}
						disabled={!canExecuteEvents}
						title={canExecuteEvents ? 'Add Event Location' : 'You do not have permission to add'}
					>
						Add
					</Button>
				</div>
				<div className={styles.list}>
					{loadingLocations ? (
						<Box
							sx={{
								display: 'flex',
								justifyContent: 'center',
								alignItems: 'center',
								minHeight: '200px',
							}}
						>
							<CircularProgress size={20} color="inherit" />
						</Box>
					) : (
						<EventLocationsList
							eventLocations={eventLocations}
							onEventLocationCreated={loadData}
							onEditClick={(location) => {
								if (!canEditEvents) {
									return;
								}
								setEditingEventLocation(location);
								setViewMode('editEventLocation');
							}}
							canEdit={canEditEvents}
							canDelete={canDeleteEvents}
						/>
					)}
				</div>
			</Card>
			<Card className="card">
				<div className={styles.header}>
					<h2 className={styles.formTitle}>Event Types</h2>
					<Button
						className={styles.addButton}
						onClick={() => setViewMode('createEventType')}
						disabled={!canExecuteEvents}
						title={canExecuteEvents ? 'Add Event Type' : 'You do not have permission to add'}
					>
						Add
					</Button>
				</div>
				<div className={styles.list}>
					{loadingTypes ? (
						<Box
							sx={{
								display: 'flex',
								justifyContent: 'center',
								alignItems: 'center',
								minHeight: '200px',
							}}
						>
							<CircularProgress size={20} color="inherit" />
						</Box>
					) : (
						<EventTypesList
							eventTypes={eventTypes}
							onEventTypeCreated={loadData}
							onEditClick={(type) => {
								if (!canEditEvents) {
									return;
								}
								setEditingEventType(type);
								setViewMode('editEventType');
							}}
							canEdit={canEditEvents}
							canDelete={canDeleteEvents}
						/>
					)}
				</div>
			</Card>
		</div>
	);
};

export default EventsSetting;
